import BaseUISystem from '../BaseUISystem';
import BaseMenu from './BaseMenu';
import GameObject, { GameObjectEvent } from '../../../GameObject/GameObject';
import { UIActions } from '../../../State/UI/UIActions';
import { applyDefaultButtonAnimation } from '../UIHelper';
import Game from '../../../Game';
import Armature from '../../../Prototype/Armature';
import Element from '../../../Prototype/UI/Element';
import TransformComponent from '../../../Component/Transform';
import SundayOfferTimer from '../../Timer/Timers/SundayOfferTimer';
import { IGameState } from '../../../State/Game/IGameState';
import UITextComponent from '../../../Component/UI/Text';
import { IAPReward } from '../../../Utility/RewardHelper';
import { report } from '../../../report';
import Renderable from '../../../Component/Renderable';
import { IAPProduct } from '../../../Utility/SDK/Drivers/IAPCatalog';
import { GameActions } from '../../../State/Game/GameActions';
import { ContextType, ResponseStatus } from '../../../State/Game/ContextType';
import LevelHelper from '../../../Utility/LevelHelper';
import Point from 'Utility/Point';
import { IAPHelper } from 'Utility/Helpers/IAP/IAPHelper';
import { PopupID } from '../../../State/UI/PopupID';
import { parseCurrency } from '../../../Utility/Currency';

export default class SundayOfferHandler extends BaseMenu {
    private timer: SundayOfferTimer;
    private timerText: UITextComponent;
    private armature: Armature;
    private graphics: PIXI.Graphics;
    private iapReward: IAPReward;

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.SUNDAY_OFFER_MENU, uiSystem);
    }

    public onCreate() {
        super.onCreate();
        this.addButtonListeners();
    }

    public onShow() {
        const state = Game.store.getState();
        const levelCost = LevelHelper.getLevelCost(
            Game.player.getHighestLevelCompleted(state)
        )

        const { dragonbonesName, animationName = 'animtion0', armatureName = 'Armature', armaturePosition = Point.Zero, productId } = IAPHelper.getWeeklySundayOfferData();

        // Handle armature/DB anim
        const DBContainer = this.element.find('background/title/background');
        this.armature = GameObject.Instantiate(Armature, {
            dragonbonesName,
            armatureName,
            animationName
        });
        const armatureTransfrom = this.armature.getComponent(TransformComponent);
        armatureTransfrom.position.x += armaturePosition.x;
        armatureTransfrom.position.y += armaturePosition.y;
        DBContainer.addChild(this.armature);

        const offerLayout = Game.resource.get("SundayOfferLayout");
        this.armature.addChild(GameObject.Instantiate(Element, offerLayout));

        // multi-line text needs reset on center
        this.element.find("background/title/text").getComponent(UITextComponent).style.align = "center";

        // Handle price localizing
        const iapData: IAPProduct = Game.sdk.getCatalog().products.find((e) => e.productId === productId);
        this.iapReward = (Game.resource.get('IAPRewards') as IAPReward[]).find((e: {id: string}) => e.id === productId);
        this.handleButtonFullPriceCrossedOutText(iapData?.price, this.iapReward, levelCost);

        // Init the timer text
        this.timerText = this.element.find("background/title/day_timer_bg/day_timer").getComponent(UITextComponent);
        this.timer = new SundayOfferTimer();
        this.timer.onUpdate = (state: IGameState) => {
            this.updateTimerText(state);
        }
        this.timer.onComplete = (state: IGameState = Game.store.getState()) => {
            if (this.timer === undefined) return;
            if (this.timer.isCompleted(state)) {
                this.updateTimerText(state, "00:00:00");
            }
        };
        this.timer.onUpdate(state);
        this.cleanupManager.add(() => {
            this.timer.stop();
        });
    }

    public onHidden() {
        this.armature?.destroy();
    }

    private readonly updateTimerText = (state: IGameState, optText?: string) => {
        if (this.timerText && this.timerText.container && this.timerText.text !== "00:00:00") {
            this.timerText.text = optText || this.timer.getFormattedCountdown(
                state
            );
        }
    }

    private readonly onShopSelected = async () => {
        UIActions.hidePopup(this.name);
        GameActions.setContext(
            ContextType.PURCHASE_PRODUCT,
            {
                product: this.iapReward,
            },
        );

        const action = await Game.store.waitFor(GameActions.restoreContext);

        if (action.payload.status === ResponseStatus.FAIL) {
            UIActions.queuePopup(PopupID.SUNDAY_OFFER_MENU, {});
        }
    };

    private readonly onCloseSelected = () => {
        UIActions.hidePopup(this.name);
    };

    private addButtonListeners() {
        const shopBtn = this.element.find('background/button_bg/button');
        const closeBtn = this.element.find('background/close_btn');

        this.initButton(shopBtn, this.onShopSelected);
        this.initButton(closeBtn, this.onCloseSelected);
    }

    private initButton(button: GameObject, onClick: () => void) {
        applyDefaultButtonAnimation(button);
        button.on(GameObjectEvent.SELECTED, onClick);
        this.cleanupManager.add(() => {
            button.off(GameObjectEvent.SELECTED, onClick);
        });
    }

    private handleButtonFullPriceCrossedOutText(
        value: string,
        iapData: IAPReward,
        levelCost: number
    ) {
        const buttonText = this.element.find('background/button_bg/button/text').getComponent(UITextComponent);
        const buttonText2 = this.element.find('background/button_bg/button/text2').getComponent(UITextComponent);
        buttonText2.text = String(value);

        const fullCostText: GameObject = this.element.find('background/button_bg/button/text');
        const fullCostUITextComponent = fullCostText.getComponent(UITextComponent);
        const parsedCurrency = parseCurrency(value);
        if (!parsedCurrency) {
            report('Failed to parse currency', {
                once: true,
                sampleRate: 0.1,
                tags: { localizedPrice: value },
            });
            fullCostText.getComponent(Renderable).visible = false;
            return;
        }
        const { usdPriceWithoutDiscount = 0, usdPriceScaling = 0 } = iapData;
        const conversionRate = (parsedCurrency?.value ?? Infinity) / iapData.usdPrice
        const localCostWithoutDiscount = conversionRate * usdPriceWithoutDiscount;
        const localCostScaling = conversionRate * usdPriceScaling;
        const fullCost = this.getFullCost(localCostWithoutDiscount, localCostScaling, levelCost)

        const language = Game.sdk.getLanguage();
        const fullCostStr = fullCost.toLocaleString(language, {
            maximumFractionDigits: 2,
        });

        if ((parseCurrency(fullCostStr)?.value ?? -Infinity) <= parsedCurrency.value) {
            // Sam: if you're here, it's because the pricing is not fully configured; check IAPRewards.json
            report('Failed to convert full price', {
                once: true,
                sampleRate: 0.1,
                tags: {
                    localizedPrice: value,
                    parsedPrice: parsedCurrency.value,
                    usdPriceWithoutDiscount: iapData.usdPriceWithoutDiscount,
                    usdPrice: iapData.usdPrice,
                    fullCostStr,
                    language,
                    localCostWithoutDiscount,
                    localCostScaling,
                    conversionRate
                },
            });
            fullCostText.getComponent(Renderable).visible = false;
            return;
        }

        fullCostUITextComponent.text =
            parsedCurrency.beforeValue + fullCostStr + parsedCurrency.afterValue;

        // Art/Design has changed their mind about 3 times on the price strikethrough style
        // I give up; it's staying hardcoded now.
        if (!this.graphics) {
            this.graphics = new PIXI.Graphics();
        }
        this.graphics.clear();
        this.graphics.beginFill(0xFAFAFA);
        this.graphics.lineStyle(5, 0xaa0000);
        const fullCostContainerSize = {
            width: buttonText.container.width * 2,
            height: buttonText.container.height,
        };
        this.graphics.moveTo(
            0,buttonText.container.height
        );
        this.graphics.lineTo(
            fullCostContainerSize.width,0
        );
        this.graphics.endFill();
        this.graphics.alpha = 0.9;
        // this.graphics.pivot = new Point(
        //     0.5,
        //     0.5
        // );
        this.graphics.position.x = 0;
        this.graphics.position.y = fullCostContainerSize.height - 10;
        buttonText.container.addChild(this.graphics);
    }

    private getFullCost(costWithoutDiscount: number, scalingCost: number, levelCost: number): number {
        return costWithoutDiscount + (scalingCost * (levelCost / 1000))
    }
}
